import { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Stack,
    Skeleton,
    Grid,
    Button,
    Chip,
} from "@mui/material";
import {
    KeyboardArrowRight,
    ErrorOutline,
    GroupsOutlined,
    CheckCircleOutline,
    PendingOutlined,
    CurrencyExchangeOutlined,
    AssignmentTurnedInOutlined,
    ArchiveOutlined,
    Settings,
    ChangeCircle,
    SaveOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getDashboard } from "apis/controllers/dashboard";
import { useSelector } from "react-redux";
import SeasonDialog from "./SeasonDialog";
import { safeDateFormat } from "qubCommon/helperMethods/dateHelper";

const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString("el-GR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });

// --- Metric Tile ---
// Inside MetricTile usage
const MetricTile = ({ label, value, color, icon, bg }) => (
    <Box
        sx={{
            flex: "1 1 45%",
            height: "100%",
            bgcolor: bg || "#fafafa",
            borderRadius: 2,
            p: 1.5,
            minHeight: 78,
            border: "1px solid #e0e0e0",
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
            transition: "all 0.2s ease",
            "&:hover": {
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                transform: "translateY(-2px)",
            },
        }}
    >
        <Box
            sx={{
                bgcolor: color,
                borderRadius: "10px",
                p: 0.8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            {icon}
        </Box>
        <Box>
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 500,
                    color: "#424242",
                    lineHeight: 1.2,
                }}
            >
                {label}
            </Typography>
            <Typography
                variant="h6"
                sx={{
                    fontWeight: 700,
                    color: "#212121",
                    lineHeight: 1.2,
                }}
            >
                {value}
            </Typography>
        </Box>
    </Box>
);


// --- Type Card (new design) ---
const TypeCard = ({ data, lottery }) => {
    const getStat = (...keys) => {
        const value = keys
            .map((key) => data?.[key])
            .find((item) => item !== undefined && item !== null);

        return value ?? 0;
    };

    const totalApplications = getStat("allApplCnt", "totalApplCnt", "totalCnt");

    const metrics = [
        {
            label: "Έγκυρες",
            value: getStat("validApplCnt"),
            color: "#E0F2F1",
            icon: <AssignmentTurnedInOutlined sx={{ color: "#26A69A" }} fontSize="small" />,
        },
        {
            label: "Υποβληθείσες",
            value: getStat("submittedCnt", "submittedApplCnt"),
            color: "#E3EAFD",
            icon: <GroupsOutlined sx={{ color: "#5C6BC0" }} fontSize="small" />,
        },
        {
            label: "Σε Έλεγχο",
            value: getStat("underReviewCnt", "underReviewApplCnt"),
            color: "#FFF3E0",
            icon: <PendingOutlined sx={{ color: "#FFA000" }} fontSize="small" />,
        },
        {
            label: "Στον Δικαιούχο (Επιστροφή)",
            value: getStat("forResubmitApplCnt"),
            color: "#E0F7FA",
            icon: <ChangeCircle sx={{ color: "#00ACC1" }} fontSize="small" />,
        },
        {
            label: "Προ-εγκεκριμένες",
            value: getStat("preApprovedApplCnt"),
            color: "#E8F5E9",
            icon: <CheckCircleOutline sx={{ color: "#43A047" }} fontSize="small" />,
        },
        {
            label: "Προς Πληρωμή",
            value: getStat("ready4PmtApplCnt"),
            color: "#EDE7F6",
            icon: <CurrencyExchangeOutlined sx={{ color: "#7E57C2" }} fontSize="small" />,
        },
        {
            label: "Ολοκλήρωση καταβολής",
            value: getStat("payCompletedApplCnt"),
            color: "#E8F5E9",
            icon: <CheckCircleOutline sx={{ color: "#2E7D32" }} fontSize="small" />,
        },
        {
            label: "Σε αρχείο",
            value: getStat("archivedApplCnt", "archiveApplCnt", "rejectedApplCnt"),
            color: "#FFEBEE",
            icon: <ArchiveOutlined sx={{ color: "#E53935" }} fontSize="small" />,
        },
        {
            label: "Αποθηκευμένες",
            value: getStat("savedDeletedApplCnt"),
            color: "#ECEFF1",
            icon: <SaveOutlined sx={{ color: "#546E7A" }} fontSize="small" />,
        },
    ];


    return (
        <Card
            sx={{
                height: "100%",
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                    boxShadow: "0 8px 28px rgba(0,0,0,0.16)",
                    transform: "translateY(-4px)",
                },
            }}
        >
            <Box
                sx={{
                    background: "#BBDEFB", // "linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)",
                    color: "text.primary",
                    px: 3,
                    py: 2.2,
                    borderBottom: "1px solid rgba(0,0,0,0.05)",
                }}
            >

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h3" sx={{ fontWeight: 700 }}>
                            {data.atpDescr}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Περίοδος Υποβολής:{" "}
                            <Typography component="span" sx={{ fontWeight: 600 }}>
                                {formatDate(data.atsDateFrom)} – {formatDate(data.atsDateTo)}
                            </Typography>
                        </Typography>
                    </Box>
                    <GroupsOutlined sx={{ fontSize: 32, opacity: 0.9 }} />
                </Stack>
            </Box>

            {/* Content */}
            <CardContent sx={{ p: 3 }}>
                <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ mb: 2 }}
                >
                    <Chip
                        label={`Συνολικές: ${totalApplications}`}
                        variant="outlined"
                        sx={{ fontWeight: 700, bgcolor: "#F5F7FB" }}
                    />
                    <Chip
                        label={`Όριο Αιτήσεων: ${data.atsLimit}`}
                        variant="outlined"
                        sx={{ fontWeight: 700, bgcolor: "#FAFAFA" }}
                    />
                </Stack>

                <Grid container spacing={1.5}>
                    {metrics.map((m, i) => (
                        <Grid item xs={12} sm={6} md={6} lg={4} key={i}>
                            <MetricTile {...m} />
                        </Grid>
                    ))}
                </Grid>

                {/* Lottery section inside */}
                {lottery && (
                    <Box
                        sx={{
                            mt: 3,
                            p: 2,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor:
                                lottery.status.includes("Δεν απαιτείται") ? "success.50" : "warning.50",
                        }}
                    >
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="subtitle2" fontWeight={600}>
                                    Κατάσταση Κλήρωσης
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Αιτήσεις: {lottery.appCount} / Όριο: {lottery.limit}
                                </Typography>
                            </Box>
                            <Chip
                                label={lottery.status}
                                color={
                                    lottery.status.includes("Δεν απαιτείται") ? "success" : "warning"
                                }
                                variant="filled"
                            />
                        </Stack>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

// --- Main Dashboard ---
const Dashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);


    const handleSeasonChange = (newSeason) => {
        // dispatch(setSeasonId(newSeason));
        window.location.reload(); // or refetch dashboard data
    };

    const { seasonId } = useSelector(state => state.auth)


    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await getDashboard({ season: seasonId });
                if (response?.status === "success") {
                    setData(response.data.stats);
                    setLastUpdated(new Date());
                } else {
                    setError("Αδυναμία φόρτωσης δεδομένων πίνακα ελέγχου");
                }
            } catch (err) {
                console.error("Dashboard fetch error:", err);
                setError("Αδυναμία φόρτωσης δεδομένων πίνακα ελέγχου");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                    {[1, 2].map((i) => (
                        <Grid item xs={12} md={6} key={i}>
                            <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 3 }} />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Card sx={{ bgcolor: "error.light", color: "error.dark" }}>
                    <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <ErrorOutline color="error" />
                            <Typography>{error}</Typography>
                            <Button
                                variant="contained"
                                color="error"
                                size="small"
                                sx={{ ml: "auto" }}
                                onClick={() => window.location.reload()}
                            >
                                Επαναφόρτωση
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    const clearingStats = data?.clearingStats || [];
    const lotteryStats = data?.lotteryStats || [];

    return (
        <Box sx={{ p: 3, maxWidth: 1400, mx: "auto", minHeight: '70vh' }}>
            {/* Header */}
            <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                spacing={2}
                mb={3}
            >
                <Stack direction="column" spacing={0.5}>
                    <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                        <Typography variant="h3">ΛΑΕ Μητέρες – Επισκόπηση {seasonId}</Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            endIcon={<ChangeCircle />}
                            sx={{
                                px: 2,
                                borderRadius: 3,
                                textTransform: "none",
                                fontWeight: 600,
                                boxShadow: "0 4px 10px rgba(25,118,210,0.3)",
                            }}
                            onClick={() => setDialogOpen(true)}
                        >
                            Αλλαγή Έτους
                        </Button>
                        <SeasonDialog
                            open={dialogOpen}
                            onClose={() => setDialogOpen(false)}
                            currentSeason={seasonId}
                            onChange={handleSeasonChange}
                        />
                    </Stack>

                    {lastUpdated && (
                        <Typography variant="body2" color="text.secondary">
                            Τελευταία ενημέρωση:{" "}
                            <Typography component="span" fontWeight={500}>
                                {safeDateFormat(lastUpdated, 'dd/MM/yyyy HH:mm')}
                            </Typography>
                        </Typography>
                    )}
                </Stack>

                <Button
                    variant="contained"
                    endIcon={<KeyboardArrowRight />}
                    onClick={() => navigate("/govnr/clearings")}
                    sx={{
                        px: 2,
                        borderRadius: 3,
                        textTransform: "none",
                        fontWeight: 600,
                        boxShadow: "0 4px 10px rgba(25,118,210,0.3)",
                    }}
                >
                    Διαχείριση Ελέγχων
                </Button>
            </Stack>

            {/* Type Cards */}
            <Grid container spacing={3} mb={4}>
                {clearingStats.map((c, idx) => {
                    const lottery = lotteryStats.find((l) => l.type === c.atpDescr);
                    return (
                        <Grid item xs={12} md={6} key={idx}>
                            <TypeCard data={c} lottery={lottery} />
                        </Grid>
                    );
                })}
            </Grid>

            {/* Bottom button */}
            <Box sx={{ textAlign: "center", mt: 4 }}>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    endIcon={<KeyboardArrowRight />}
                    onClick={() => navigate("/govnr/applications")}
                    sx={{
                        px: 2,
                        borderRadius: 3,
                        textTransform: "none",
                        fontWeight: 600,
                        boxShadow: "0 4px 10px rgba(25,118,210,0.3)",
                    }}
                >
                    Προβολή Αιτήσεων
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    endIcon={<Settings />}
                    onClick={() => navigate("/govnr/program-settings")}
                    sx={{
                        px: 2,
                        ml: 1,
                        borderRadius: 3,
                        textTransform: "none",
                        fontWeight: 600,
                        boxShadow: "0 4px 10px rgba(25,118,210,0.3)",
                    }}
                >
                    Ρυθμίσεις Προγράμματος
                </Button>
            </Box>
        </Box>
    );
};

export default Dashboard;
